export function formatDashboardMoney(value) {
  if (typeof value !== 'number') return value
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)
}

export function formatDateLabel(value) {
  if (!value) return 'Scheduled'
  return new Date(value).toLocaleString('en-US', {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function normalizeLabel(value, fallback = 'not ready') {
  return value ? String(value).replace(/-/g, ' ') : fallback
}

export function getVaultTone(status) {
  if (['payment-pending', 'outbid'].includes(status)) return 'danger'
  if (['weekly-hold', 'leading', 'live'].includes(status)) return 'warning'
  if (['paid', 'shipped', 'delivered', 'won'].includes(status)) return 'success'
  return 'neutral'
}

export function buildVaultItems(dashboardData, resolveImage = () => '') {
  if (!dashboardData) return []

  const auctionById = new Map((dashboardData.auctions || []).map((auction) => [auction.id, auction]))
  const listingById = new Map((dashboardData.buyNowListings || []).map((listing) => [listing.id, listing]))
  const seen = new Set()

  const addUnique = (items, item) => {
    if (seen.has(item.id)) return items
    seen.add(item.id)
    return [...items, item]
  }

  const orderItems = (dashboardData.orders || []).reduce((items, order) => {
    const auction = order.auctionId ? auctionById.get(order.auctionId) : null
    const listing = order.listingId ? listingById.get(order.listingId) : null
    const source = auction || listing

    return addUnique(items, {
      id: order.id,
      title: order.itemTitle,
      detail: order.auctionId ? 'Auction win' : listing?.category || 'Buy Now purchase',
      value: formatDashboardMoney(order.total),
      lane: order.weeklyHold ? 'Weekly Hold' : order.status === 'payment-pending' ? 'Payment Due' : 'Order',
      status: normalizeLabel(order.status),
      tone: getVaultTone(order.status),
      image: resolveImage(source?.images?.[0]),
      meta: `Shipping ${normalizeLabel(order.shippingStatus)} / due ${order.paymentDueAt ? new Date(order.paymentDueAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'none'}`,
    })
  }, [])

  const activeBidItems = (dashboardData.auctions || [])
    .filter((auction) => auction.highBidderId === dashboardData.account?.id && auction.status !== 'closed')
    .reduce((items, auction) => addUnique(items, {
      id: `bid_${auction.id}`,
      title: auction.title,
      detail: auction.subtitle,
      value: formatDashboardMoney(auction.currentBid),
      lane: 'Leading Bid',
      status: 'leading',
      tone: 'warning',
      image: resolveImage(auction.images?.[0]),
      meta: `${normalizeLabel(auction.type, 'auction')} closes ${formatDateLabel(auction.endsAt)}`,
    }), orderItems)

  return (dashboardData.watchlist || []).reduce((items, auction) => addUnique(items, {
    id: `watch_${auction.id}`,
    title: auction.title,
    detail: auction.subtitle,
    value: formatDashboardMoney(auction.currentBid),
    lane: 'Watchlist',
    status: normalizeLabel(auction.status, 'watched'),
    tone: getVaultTone(auction.status),
    image: resolveImage(auction.images?.[0]),
    meta: `${auction.bidCount || 0} bids / next ${formatDashboardMoney(auction.nextBid || auction.currentBid || 0)}`,
  }), activeBidItems)
}

export function buildVaultSummary({ bundleData, dashboardData, totalItems }) {
  if (!dashboardData) {
    return {
      totalItems: 0,
      dueNow: 0,
      weeklyHold: 0,
      watchlist: 0,
      vaultValue: '$0',
      checkout: 'Loading',
    }
  }

  const orders = dashboardData.orders || []
  const totalOrderValue = orders.reduce((sum, order) => sum + Number(order.total || 0), 0)

  return {
    totalItems,
    dueNow: orders.filter((order) => order.status === 'payment-pending').length,
    weeklyHold: orders.filter((order) => order.weeklyHold).length,
    watchlist: dashboardData.watchlist?.length || 0,
    vaultValue: formatDashboardMoney(totalOrderValue),
    checkout: bundleData?.checkoutDate || 'Friday',
  }
}
